import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faChevronDown, faChevronRight, faDrawPolygon, faDownload } from '@fortawesome/free-solid-svg-icons';
import EditBusinessUnitModal from './EditBusinessUnitModal';
import FarmModal from './FarmModal';
import FieldMapModal from './FieldMapModal';
import EditFieldModal from './EditFieldModal';
import ImportFarmsModal from './ImportFarmsModal';
import SetupMap from './SetupMap';
import EditOrganizationModal from './EditOrganizationModal';
import { API_ENDPOINTS } from '../constants/api';
import './SetupTree.css';

interface BusinessUnit {
  business_id: string;
  business: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  phone?: string;
  is_current?: boolean;
}

interface Farm {
  farm_id: string;
  farm: string;
  business_id: string;
  is_current?: boolean;
}

interface Field {
  field_id: string;
  fieldname: string;
  farm_id: string;
  acres: number;
  geojson: string;
  state: string;
  county: string;
  climate_zone: string;
  cdl2024?: string; // Optional - only used when creating fields from map
  is_current?: boolean;
  created_at: string;
  updated_at: string;
}

interface SetupTreeProps {
  userToken: string;
  organizationName: string;
  selectedOrganizationId?: string; // Add this prop for Super User context
  organization?: any;
  businessUnits?: BusinessUnit[];
  farms?: Farm[];
  fields?: Field[];
  isLoading?: boolean;
  onDataUpdate?: (type: string, data: any) => void;
  onDataRefresh?: () => void;
  onOrganizationDeleted?: () => void;
}

const SetupTree: React.FC<SetupTreeProps> = ({
  userToken,
  organizationName,
  selectedOrganizationId,
  organization: initialOrganization,
  businessUnits: initialBusinessUnits = [],
  farms: initialFarms = [],
  fields: initialFields = [],
  isLoading: externalLoading = false,
  onDataUpdate,
  onDataRefresh,
  onOrganizationDeleted,
}) => {
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>(initialBusinessUnits);
  const [farms, setFarms] = useState<Farm[]>(initialFarms);
  const [fields, setFields] = useState<Field[]>(initialFields);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<{ type: 'business' | 'farm' | 'field', id: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Track what data has been loaded
  const [dataLoaded, setDataLoaded] = useState({
    businessUnits: initialBusinessUnits.length > 0,
    farms: initialFarms.length > 0,
    fields: initialFields.length > 0
  });

  // Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBusinessUnit, setEditingBusinessUnit] = useState<BusinessUnit | null>(null);
  const [isAddingNewBusinessUnit, setIsAddingNewBusinessUnit] = useState(false);

  // Farm modal state
  const [farmModalOpen, setFarmModalOpen] = useState(false);
  const [editingFarm, setEditingFarm] = useState<Farm | null>(null);
  const [isEditFarm, setIsEditFarm] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');

  // Field map modal state
  const [fieldMapModalOpen, setFieldMapModalOpen] = useState(false);
  const [selectedFarmId, setSelectedFarmId] = useState('');
  const [selectedFarmName, setSelectedFarmName] = useState('');
  const [fieldMapModalMode, setFieldMapModalMode] = useState<'add' | 'addBoundary'>('add');
  const [targetFieldForBoundary, setTargetFieldForBoundary] = useState<Field | null>(null);

  // Edit field modal state
  const [editFieldModalOpen, setEditFieldModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<Field | null>(null);

  // Import farms modal state
  const [importFarmsModalOpen, setImportFarmsModalOpen] = useState(false);
  const [importBusinessUnitId, setImportBusinessUnitId] = useState('');
  const [importBusinessUnitName, setImportBusinessUnitName] = useState('');

  // Organization modal state
  const [editOrgModalOpen, setEditOrgModalOpen] = useState(false);
  const [organization, setOrganization] = useState<any>(initialOrganization);

  // Toggle panel state
  const [showNonCurrent, setShowNonCurrent] = useState(false);


  // Load data on component mount (only if not provided externally)
  useEffect(() => {
    if (initialBusinessUnits.length === 0 && initialFarms.length === 0 && initialFields.length === 0) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, []);

  // Expand all business units and farms by default
  useEffect(() => {
    if (businessUnits.length > 0 || farms.length > 0) {
      const businessUnitIds = businessUnits.map(bu => bu.business_id);
      const farmIds = farms.map(farm => farm.farm_id);
      const allIds = [...businessUnitIds, ...farmIds];
      setExpandedItems(new Set(allIds));
    }
  }, [businessUnits, farms]);

  // Handle external data updates (when parent component passes new data)
  useEffect(() => {
    
    // Always sync with props, regardless of length
    setBusinessUnits(initialBusinessUnits);
    setFarms(initialFarms);
    setFields(initialFields);
    
    // Update dataLoaded flags based on whether we have data
    setDataLoaded({
      businessUnits: initialBusinessUnits.length > 0,
      farms: initialFarms.length > 0,
      fields: initialFields.length > 0
    });
    
    // If we have data from Dashboard, stop loading
    if (initialBusinessUnits.length > 0 || initialFarms.length > 0 || initialFields.length > 0) {
      setIsLoading(false);
    }
  }, [initialBusinessUnits, initialFarms, initialFields]);

  // Auto-select first farm when data is loaded (if no item is currently selected)
  useEffect(() => {
    
    // Only auto-select if no item is currently selected and we're not loading
    if (!selectedItem && !isLoading) {
      
      // Add a small delay to ensure component is fully rendered
      const timer = setTimeout(() => {
        if (farms.length > 0) {
          // Find the first farm (prioritize current farms if available)
          const firstFarm = farms.find(farm => farm.is_current) || farms[0];
          if (firstFarm) {
            setSelectedItem({ type: 'farm', id: firstFarm.farm_id });
          }
        } else if (businessUnits.length > 0) {
          // If no farms, select the first business unit (prioritize current ones)
          const firstBusinessUnit = businessUnits.find(bu => bu.is_current) || businessUnits[0];
          if (firstBusinessUnit) {
            setSelectedItem({ type: 'business', id: firstBusinessUnit.business_id });
          }
        } else {
          //
        }
      }, 100); // Small delay to ensure component is fully rendered

      return () => clearTimeout(timer);
    }
  }, [farms, businessUnits, selectedItem, isLoading]);

  // Handle case where selected item no longer exists after data refresh
  useEffect(() => {
    if (selectedItem && !isLoading) {
      let itemExists = false;
      
      if (selectedItem.type === 'farm') {
        itemExists = farms.some(farm => farm.farm_id === selectedItem.id);
      } else if (selectedItem.type === 'business') {
        itemExists = businessUnits.some(bu => bu.business_id === selectedItem.id);
      } else if (selectedItem.type === 'field') {
        itemExists = fields.some(field => field.field_id === selectedItem.id);
      }
      
      // If the selected item no longer exists, clear the selection to trigger auto-selection
      if (!itemExists) {
        setSelectedItem(null);
      }
    }
  }, [farms, businessUnits, fields, selectedItem, isLoading]);

  const loadData = async () => {
    
    // Check if we need to load anything
    const needsLoading = !dataLoaded.businessUnits || !dataLoaded.farms || !dataLoaded.fields;
    
    if (!needsLoading) {
      //
      setIsLoading(false);
      return;
    }

    //
    setIsLoading(true);
    setError('');

    try {
      // Load business units if not loaded
      if (!dataLoaded.businessUnits) {
        const buResponse = await fetch(API_ENDPOINTS.BUSINESS_UNITS, {
          headers: {
            'Authorization': `Bearer ${userToken}`,
          },
        });

        if (!buResponse.ok) throw new Error('Failed to load business units');
        const businessUnitsData = await buResponse.json();
        setBusinessUnits(businessUnitsData);
        setDataLoaded(prev => ({ ...prev, businessUnits: true }));
      }

      // Load farms if not loaded
      if (!dataLoaded.farms) {
        const farmsResponse = await fetch(API_ENDPOINTS.FARMS, {
          headers: {
            'Authorization': `Bearer ${userToken}`,
          },
        });
        if (!farmsResponse.ok) throw new Error('Failed to load farms');
        const farmsData = await farmsResponse.json();
        setFarms(farmsData);
        setDataLoaded(prev => ({ ...prev, farms: true }));
      }

      // Load fields if not loaded
      if (!dataLoaded.fields) {
        const organizationParam = selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
        const fieldsResponse = await fetch(`${API_ENDPOINTS.FIELDS}${organizationParam}`, {
          headers: {
            'Authorization': `Bearer ${userToken}`,
          },
        });
        if (!fieldsResponse.ok) throw new Error('Failed to load fields');
        const fieldsData = await fieldsResponse.json();
        setFields(fieldsData);
        setDataLoaded(prev => ({ ...prev, fields: true }));
      }

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to refresh specific data types
  const refreshData = async (types: ('businessUnits' | 'farms' | 'fields')[] = []) => {
    
    
    if (types.length === 0) {
      // Reset all loaded flags to force reload
      setDataLoaded({ businessUnits: false, farms: false, fields: false });
    } else {
      // Reset only specified types
      setDataLoaded(prev => {
        const newState = { ...prev };
        types.forEach(type => {
          newState[type] = false;
        });
        return newState;
      });
    }

    // Force a small delay to ensure state updates are processed
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Reload data
    
    await loadData();
  };

  

  // Alternative refresh function that directly fetches and updates data
  const forceRefreshData = async (types: ('businessUnits' | 'farms' | 'fields')[] = []) => {
    
    
    try {
      setIsLoading(true);
      
      // Directly fetch and update the specified data types
      if (types.includes('farms')) {
        const farmsResponse = await fetch(API_ENDPOINTS.FARMS, {
          headers: {
            'Authorization': `Bearer ${userToken}`,
          },
        });
        if (!farmsResponse.ok) throw new Error('Failed to load farms');
        const farmsData = await farmsResponse.json();
        setFarms(farmsData);
        setDataLoaded(prev => ({ ...prev, farms: true }));
      }
      
      if (types.includes('fields')) {
        const organizationParam = selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
        const fieldsResponse = await fetch(`${API_ENDPOINTS.FIELDS}${organizationParam}`, {
          headers: {
            'Authorization': `Bearer ${userToken}`,
          },
        });
        if (!fieldsResponse.ok) throw new Error('Failed to load fields');
        const fieldsData = await fieldsResponse.json();
        setFields(fieldsData);
        setDataLoaded(prev => ({ ...prev, fields: true }));
      }
      
    } catch (err) {
      console.error('Error in forceRefreshData:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh data');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleAddBusinessUnit = () => {
    setEditingBusinessUnit(null); // null means we're creating a new one
    setIsAddingNewBusinessUnit(true);
    setEditModalOpen(true);
  };

  const handleEditBusinessUnit = (businessUnit: BusinessUnit) => {
    setEditingBusinessUnit(businessUnit);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditingBusinessUnit(null);
  };

  const handleEditModalSave = (updatedBusinessUnit: BusinessUnit) => {
    if (isAddingNewBusinessUnit) {
      // Adding new business unit
      const newBusinessUnits = [...businessUnits, updatedBusinessUnit];
      setBusinessUnits(newBusinessUnits);
      onDataUpdate?.('businessUnits', newBusinessUnits);
    } else {
      // Updating existing business unit
      const updatedBusinessUnits = businessUnits.map(bu => bu.business_id === updatedBusinessUnit.business_id ? updatedBusinessUnit : bu);
      setBusinessUnits(updatedBusinessUnits);
      onDataUpdate?.('businessUnits', updatedBusinessUnits);
    }
  };



  const handleAddFarm = (businessId: string) => {
    setSelectedBusinessId(businessId);
    setEditingFarm(null);
    setIsEditFarm(false);
    setFarmModalOpen(true);
  };

  const handleImportFarms = (businessId: string) => {
    const businessUnit = businessUnits.find(bu => bu.business_id === businessId);
    if (businessUnit) {
      setImportBusinessUnitId(businessId);
      setImportBusinessUnitName(businessUnit.business);
      setImportFarmsModalOpen(true);
    }
  };

  const handleImportFarmsModalClose = () => {
    setImportFarmsModalOpen(false);
    setImportBusinessUnitId('');
    setImportBusinessUnitName('');
  };



  const handleEditFarm = (farm: Farm) => {
    setSelectedBusinessId(farm.business_id);
    setEditingFarm(farm);
    setIsEditFarm(true);
    setFarmModalOpen(true);
  };

  const handleFarmModalClose = () => {
    setFarmModalOpen(false);
    setEditingFarm(null);
    setSelectedBusinessId('');
  };

  const handleFarmModalSave = (savedFarm: Farm) => {
    if (isEditFarm) {
      // Update existing farm
      const updatedFarms = farms.map(f => f.farm_id === savedFarm.farm_id ? savedFarm : f);
      setFarms(updatedFarms);
      onDataUpdate?.('farms', updatedFarms);
    } else {
      // Add new farm
      const updatedFarms = [...farms, savedFarm];
      setFarms(updatedFarms);
      onDataUpdate?.('farms', updatedFarms);
    }
  };

  const handleAddField = (farmId: string, farmName: string) => {
    setSelectedFarmId(farmId);
    setSelectedFarmName(farmName);
    setFieldMapModalMode('add');
    setTargetFieldForBoundary(null);
    setFieldMapModalOpen(true);
  };

  const handleAddFieldBoundary = (field: Field) => {
    setSelectedFarmId(field.farm_id);
    const farm = farms.find(f => f.farm_id === field.farm_id);
    setSelectedFarmName(farm?.farm || 'Unknown Farm');
    setFieldMapModalMode('addBoundary');
    setTargetFieldForBoundary(field);
    setFieldMapModalOpen(true);
  };

  const handleEditField = (field: Field) => {
    setEditingField(field);
    setEditFieldModalOpen(true);
  };

  const handleFieldMapModalClose = () => {
    setFieldMapModalOpen(false);
    setSelectedFarmId('');
    setSelectedFarmName('');
    setFieldMapModalMode('add');
    setTargetFieldForBoundary(null);
  };

  const handleEditOrganization = () => {
    
    setEditOrgModalOpen(true);

  };

  const handleEditOrgModalClose = () => {
    setEditOrgModalOpen(false);
  };

  const handleEditOrgModalSave = async (updatedOrg: any) => {
    try {
      // Call the API to update the organization
      const response = await fetch(`${API_ENDPOINTS.ORGANIZATIONS}/${organization.organization_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify(updatedOrg),
      });

      if (!response.ok) {
        throw new Error('Failed to update organization');
      }

      const updatedOrganization = await response.json();
      setOrganization(updatedOrganization);
      
      // Notify parent component of the update
      onDataUpdate?.('organization', updatedOrganization);
      
      setEditOrgModalOpen(false);
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  };

  const handleOrganizationDeleted = () => {
    setEditOrgModalOpen(false);
    setOrganization(null);
    setBusinessUnits([]);
    setFarms([]);
    setFields([]);
    onOrganizationDeleted?.();
  };

  const handleFieldMapModalSave = (savedField: Field) => {
    let updatedFields;
    
    if (fieldMapModalMode === 'addBoundary' && targetFieldForBoundary) {
      // Update existing field with new boundary data
      updatedFields = fields.map(f => f.field_id === savedField.field_id ? savedField : f);
      console.log('🔄 Updated field with boundary:', savedField.fieldname);
    } else {
      // Add new field
      updatedFields = [...fields, savedField];
      console.log('➕ Added new field:', savedField.fieldname);
    }
    
    setFields(updatedFields);
    onDataUpdate?.('fields', updatedFields);
    
    // For boundary updates, we don't need a full refresh since we have the updated field data
    // Only refresh if it was a new field creation (not boundary update)
    if (fieldMapModalMode === 'add') {
      // For new fields, do a targeted refresh of just fields data
      setTimeout(async () => {
        await forceRefreshData(['fields']);
      }, 300);
    }
  };

  const handleEditFieldModalClose = () => {
    setEditFieldModalOpen(false);
    setEditingField(null);
  };

  const handleEditFieldModalSave = (updatedField: Field) => {
    const updatedFields = fields.map(f => f.field_id === updatedField.field_id ? updatedField : f);
    setFields(updatedFields);
    onDataUpdate?.('fields', updatedFields);
  };

  const handleBusinessUnitDelete = (businessUnitId: string) => {
    // Remove business unit and all its farms and fields
    const updatedBusinessUnits = businessUnits.filter(bu => bu.business_id !== businessUnitId);
    const businessUnitFarms = farms.filter(f => f.business_id === businessUnitId);
    const businessUnitFarmIds = businessUnitFarms.map(f => f.farm_id);
    const updatedFarms = farms.filter(f => f.business_id !== businessUnitId);
    const updatedFields = fields.filter(f => !businessUnitFarmIds.includes(f.farm_id));
    
    setBusinessUnits(updatedBusinessUnits);
    setFarms(updatedFarms);
    setFields(updatedFields);
    onDataUpdate?.('businessUnits', updatedBusinessUnits);
    onDataUpdate?.('farms', updatedFarms);
    onDataUpdate?.('fields', updatedFields);
  };

  const handleFarmDelete = (farmId: string) => {
    // Remove farm and all its fields
    const updatedFarms = farms.filter(f => f.farm_id !== farmId);
    const updatedFields = fields.filter(f => f.farm_id !== farmId);
    
    setFarms(updatedFarms);
    setFields(updatedFields);
    onDataUpdate?.('farms', updatedFarms);
    onDataUpdate?.('fields', updatedFields);
  };

  const handleFieldDelete = (fieldId: string) => {
    // Remove field
    const updatedFields = fields.filter(f => f.field_id !== fieldId);
    setFields(updatedFields);
    onDataUpdate?.('fields', updatedFields);
  };

  const handleFieldUpdate = async (fieldId: string, updates: Partial<Field>) => {
    try {

      // Make API call to update the field
      const response = await fetch(`${API_ENDPOINTS.FIELDS}/${fieldId}?organization_id=${selectedOrganizationId || ''}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error('Failed to update field');
      }

      const updatedField = await response.json();

      // Update local state with the response from the server
      const updatedFields = fields.map(f =>
        f.field_id === fieldId ? updatedField : f
      );
      setFields(updatedFields);
      onDataUpdate?.('fields', updatedFields);

    } catch (error) {
      console.error('❌ Error updating field:', error);
      // You might want to show an error message to the user here
    }
  };

  const getFarmsForBusinessUnit = (businessUnitId: string) => {
    return farms.filter(farm => farm.business_id === businessUnitId);
  };

  const getFieldsForFarm = (farmId: string) => {
    return fields.filter(field => field.farm_id === farmId);
  };

  // Toggle panel functions
  const handleToggleNonCurrent = () => {
    setShowNonCurrent(!showNonCurrent);
  };

  const handleExpandAll = () => {
    const allIds = [
      ...businessUnits.map(bu => bu.business_id),
      ...farms.map(farm => farm.farm_id)
    ];
    setExpandedItems(new Set(allIds));
  };

  const handleCollapseAll = () => {
    setExpandedItems(new Set());
  };

  // Filter and sort functions for non-current items
  // All items are automatically sorted alphabetically by name
  const getFilteredBusinessUnits = () => {
    let filtered = businessUnits;
    if (!showNonCurrent) {
      filtered = businessUnits.filter(bu => bu.is_current !== false);
    }
    return filtered.sort((a, b) => a.business.localeCompare(b.business));
  };

  const getFilteredFarms = (businessUnitId: string) => {
    let allFarms = farms.filter(farm => farm.business_id === businessUnitId);
    if (!showNonCurrent) {
      allFarms = allFarms.filter(farm => farm.is_current !== false);
    }
    return allFarms.sort((a, b) => a.farm.localeCompare(b.farm));
  };

  const getFilteredFields = (farmId: string) => {
    let allFields = fields.filter(field => field.farm_id === farmId);
    if (!showNonCurrent) {
      allFields = allFields.filter(field => field.is_current !== false);
    }
    return allFields.sort((a, b) => a.fieldname.localeCompare(b.fieldname));
  };

  if (isLoading) {
    return (
      <div className="setup-tree">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="setup-tree">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="setup-tree">
      <div className="tree-header">
        <div className="header-content">
          <div className="header-top-left">
            <a
              href="https://ravahcarbon.tawk.help/article/how-to-set-up-your-farms-and-fields"
              target="_blank"
              rel="noopener noreferrer"
              className="help-button"
              title="Open Knowledge Base article: How to Set Up Your Farms and Fields"
            >
              Help
            </a>
          </div>
          <p>
            Manage your business units, farms, and fields<br />
          <span className="small-text">Click on a business unit, farm, or field to view and edit details or add new farms and fields</span>
          </p>
          
          <div className="header-buttons">
            <button
              className="btn-edit-organization"
              onClick={handleEditOrganization}
              title="Edit Organization Details"
            >
              Edit Organization
            </button>
            <button
              className="btn-add-business-unit"
              onClick={handleAddBusinessUnit}
            >
              Add New Business Unit
            </button>
          </div>
        </div>
      </div>

      <div className="tree-container">
        <div className="tree-content">
          {/* Scrollable container for tree items */}
          <div className="tree-items-container">
            {/* Business Units */}
            {getFilteredBusinessUnits().map((businessUnit) => (
            <div key={businessUnit.business_id} className="tree-item business-item">
              <div className="tree-item-content">
                <button
                  className={`expand-button ${expandedItems.has(businessUnit.business_id) ? 'expanded' : ''}`}
                  onClick={() => toggleExpanded(businessUnit.business_id)}
                >
                  <FontAwesomeIcon
                    icon={expandedItems.has(businessUnit.business_id) ? faChevronDown : faChevronRight}
                  />
                </button>
                <span
                  onClick={() => setSelectedItem({ type: 'business', id: businessUnit.business_id })}
                  className="tree-item-name"
                >
                  {businessUnit.business}
                </span>
                <div className="tree-item-actions">
                  {getFilteredFarms(businessUnit.business_id).length === 0 ? (
                    // Show import options when no farms exist
                    <div className="farm-import-options">
                      <button
                        className="btn-add"
                        onClick={() => handleAddFarm(businessUnit.business_id)}
                        title="Add a new farm manually"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        Add Farm
                      </button>
                      <button
                        className="btn-import"
                        onClick={() => handleImportFarms(businessUnit.business_id)}
                        title="Import farms and fields from file"
                      >
                        <FontAwesomeIcon icon={faDownload} />
                        Import
                      </button>
                    </div>
                  ) : (
                    // Show normal farm button when farms exist
                    <button
                      className="btn-add"
                      onClick={() => handleAddFarm(businessUnit.business_id)}
                      title="Add a new farm"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Add Farm
                    </button>
                  )}
                  <button
                    className="btn-edit"
                    onClick={() => handleEditBusinessUnit(businessUnit)}
                    title="Edit Business Unit Details"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                </div>
              </div>

              {/* Farms */}
              {expandedItems.has(businessUnit.business_id) && (
                <div className="tree-children">
                  {getFilteredFarms(businessUnit.business_id).map((farm) => (
                    <div key={farm.farm_id} className="tree-item farm-item">
                      <div className="tree-item-content">
                        <button
                          className={`expand-button ${expandedItems.has(farm.farm_id) ? 'expanded' : ''}`}
                          onClick={() => toggleExpanded(farm.farm_id)}
                        >
                          <FontAwesomeIcon
                            icon={expandedItems.has(farm.farm_id) ? faChevronDown : faChevronRight}
                          />
                        </button>
                        <span
                          className="tree-item-name"
                          onClick={() => setSelectedItem({ type: 'farm', id: farm.farm_id })}
                        >
                          {farm.farm}
                          
                        </span>
                        <div className="tree-item-actions">
                          <button
                            className="btn-add"
                            onClick={() => handleAddField(farm.farm_id, farm.farm)}
                            title="Add a new field"
                          >
                            <FontAwesomeIcon icon={faPlus} />
                            Add Field
                          </button>
                          <button
                            className="btn-edit"
                            onClick={() => handleEditFarm(farm)}
                            title="Edit Farm Details"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        </div>
                      </div>

                      {/* Fields */}
                      {expandedItems.has(farm.farm_id) && (
                        <div className="tree-children">
                          {getFilteredFields(farm.farm_id).map((field) => (
                            <div key={field.field_id} className="tree-item field-item">
                              <div className="tree-item-content">
                                <span
                                  className="tree-item-name"
                                  onClick={() => setSelectedItem({ type: 'field', id: field.field_id })}
                                >
                                  {field.fieldname}
                                </span>
                                <div className="tree-item-actions">
                                  {(!field.geojson || field.geojson.trim() === '') ? (
                                    // Show "Add Boundary" button for fields without geometry
                                    <button
                                      className="btn-add"
                                      onClick={() => handleAddFieldBoundary(field)}
                                      title="Add Field Boundary"
                                    >
                                      <FontAwesomeIcon icon={faDrawPolygon} />
                                      Add Boundary
                                    </button>
                                  ) : (
                                    // Show normal edit button for fields with geometry
                                    <button
                                      className="btn-edit"
                                      onClick={() => handleEditField(field)}
                                      title="Edit Field Details"
                                    >
                                      <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          </div> {/* Close tree-items-container */}

          {/* Toggle Panel */}
          <div className="toggle-panel">
            <div className="toggle-buttons">
              <button
                className={`toggle-btn ${!showNonCurrent ? 'active' : ''}`}
                onClick={handleToggleNonCurrent}
              >
                {showNonCurrent ? 'Hide Non-Current' : 'Show Non-Current'}
              </button>
              <button
                className={`toggle-btn ${expandedItems.size > 0 ? 'active' : ''}`}
                onClick={expandedItems.size > 0 ? handleCollapseAll : handleExpandAll}
              >
                {expandedItems.size > 0 ? 'Collapse All' : 'Expand All'}
              </button>
            </div>
          </div>
        </div>

        {/* Right Panel for Details */}
        <div className="tree-details">
          {selectedItem ? (
            <div className="details-content">
              {selectedItem.type === 'field' && (
                <div className="field-details">
                  <h3>Field Details</h3>
                  {(() => {
                    const field = fields.find(f => f.field_id === selectedItem.id);
                    if (!field) return <p>Field not found</p>;
                    
                    // Get all fields from the same farm to show in map
                    const allFieldsFromFarm = fields.filter(f => f.farm_id === field.farm_id);

                    return (
                      <div>
                        <div className="field-info">
                          <p><strong>Name:</strong> {field.fieldname}</p>
                          <p><strong>Area:</strong> {(typeof field.acres === 'string' ? parseFloat(field.acres) || 0 : field.acres).toFixed(2)} acres</p>
                          <p><strong>Location:</strong> {field.county || 'Not set'}, {field.state || 'Not set'}</p>
                          {(!field.geojson || field.geojson.trim() === '') && (
                            <div style={{ 
                              marginTop: '10px', 
                              padding: '10px', 
                              backgroundColor: '#fff3cd', 
                              border: '1px solid #ffeaa7', 
                              borderRadius: '4px',
                              fontSize: '14px'
                            }}>
                              <strong>⚠️ No Field Boundary</strong>
                              <br />
                              This field was imported from Excel and doesn't have boundary coordinates yet. 
                              Use the Add Boundary button to add field boundaries.
                            </div>
                          )}
                        </div>
                        <div className="setup-tree-field-map-container">
                          <SetupMap
                            fields={allFieldsFromFarm}
                            selectedFieldId={field.field_id}
                            mapStyle="satellite"
                            showDrawingTools={true}
                            onFieldUpdate={handleFieldUpdate}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {selectedItem.type === 'farm' && (
                <div className="farm-details">
                  <h3>Farm Details</h3>
                  {(() => {
                    const farm = farms.find(f => f.farm_id === selectedItem.id);
                    const farmFields = fields.filter(f => f.farm_id === selectedItem.id);
                    if (!farm) return <p>Farm not found</p>;

                    const totalAcres = farmFields.reduce((sum, field) => sum + (typeof field.acres === 'string' ? parseFloat(field.acres) || 0 : field.acres), 0);

                    return (
                      <div>
                        <div className="farm-info">
                          <p><strong>Name:</strong> {farm.farm}</p>
                          <p><strong>Total Fields:</strong> {farmFields.length}</p>
                          <p><strong>Total Area:</strong> {totalAcres.toFixed(2)} acres</p>
                        </div>
                        <div className="setup-tree-farm-map-container">
                          <SetupMap
                            fields={farmFields}
                            mapStyle="satellite"
                            showDrawingTools={false}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {selectedItem.type === 'business' && (
                <div className="business-details">
                  <h3>Business Unit Details</h3>
                  {(() => {
                    const businessUnit = businessUnits.find(bu => bu.business_id === selectedItem.id);
                    const businessFarms = farms.filter(f => f.business_id === selectedItem.id);
                    const businessFields = fields.filter(f =>
                      businessFarms.some(farm => farm.farm_id === f.farm_id)
                    );
                    if (!businessUnit) return <p>Business Unit not found</p>;

                    const totalAcres = businessFields.reduce((sum, field) => sum + (typeof field.acres === 'string' ? parseFloat(field.acres) || 0 : field.acres), 0);

                    return (
                      <div>
                        <div className="business-info">
                          <p><strong>Total Farms:</strong> {businessFarms.length}</p>
                          <p><strong>Total Fields:</strong> {businessFields.length}</p>
                          <p><strong>Total Area:</strong> {totalAcres.toFixed(2)} acres</p>
                        </div>
                        <div className="setup-tree-business-map-container">
                          <SetupMap
                            fields={businessFields}
                            mapStyle="satellite"
                            showDrawingTools={false}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : (
            <div className="details-content">
              <h3>Details</h3>
              <p>Click on a Business, Farm or Field name to view the details</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Business Unit Modal */}
      <EditBusinessUnitModal
        businessUnit={editingBusinessUnit}
        isOpen={editModalOpen}
        onClose={handleEditModalClose}
        onSave={handleEditModalSave}
        onDelete={handleBusinessUnitDelete}
        userToken={userToken}
      />



      {/* Farm Modal */}
      <FarmModal
        farm={editingFarm}
        businessId={selectedBusinessId}
        isOpen={farmModalOpen}
        isEdit={isEditFarm}
        onClose={handleFarmModalClose}
        onSave={handleFarmModalSave}
        onDelete={handleFarmDelete}
        userToken={userToken}
        existingFarms={farms}
        selectedOrganizationId={selectedOrganizationId}
      />

      {/* Field Map Modal */}
      <FieldMapModal
        farmId={selectedFarmId}
        farmName={selectedFarmName}
        isOpen={fieldMapModalOpen}
        onClose={handleFieldMapModalClose}
        onSave={handleFieldMapModalSave}
        userToken={userToken}
        selectedOrganizationId={selectedOrganizationId}
        existingFields={fields.filter(field => field.farm_id === selectedFarmId)}
        isManualDrawMode={fieldMapModalMode === 'addBoundary'}
        targetField={targetFieldForBoundary}
      />

      {/* Edit Field Modal */}
      <EditFieldModal
        field={editingField}
        isOpen={editFieldModalOpen}
        onClose={handleEditFieldModalClose}
        onSave={handleEditFieldModalSave}
        onDelete={handleFieldDelete}
        userToken={userToken}
        selectedOrganizationId={selectedOrganizationId || ''}
      />

      {/* Import Farms Modal */}
      <ImportFarmsModal
        isOpen={importFarmsModalOpen}
        onClose={handleImportFarmsModalClose}
        onDataRefresh={async () => {
          if (onDataRefresh) {
            await onDataRefresh();
          } else {
            // Fallback to local refresh if no parent refresh function
            await forceRefreshData(['farms', 'fields']);
          }
        }}
        businessUnitId={importBusinessUnitId}
        businessUnitName={importBusinessUnitName}
        userToken={userToken}
      />

      {/* Edit Organization Modal */}
      {organization && (
        <EditOrganizationModal
          opened={editOrgModalOpen}
          onClose={handleEditOrgModalClose}
          organization={organization}
          onSave={handleEditOrgModalSave}
          onDeleted={handleOrganizationDeleted}
          userToken={userToken}
        />
      )}
    </div>
  );
};

export default SetupTree;
